import { identifierCacheFor } from '../../identifiers/cache';
import { BRAND_SYMBOL } from '../../ts-interfaces/utils/brand';
import constructResource from '../../utils/construct-resource';
import { upgradeForInternal } from '../ts-upgrade-map';
import { internalModelFactoryFor } from './internal-model-factory';

/**
  @module @ember-data/store
*/

export default class RecordDataStoreWrapper {
  constructor(_store) {
    this._store = _store;
    this[BRAND_SYMBOL] = void 0;
    this._willUpdateManyArrays = void 0;
    this._pendingManyArrayUpdates = void 0;
    this._willUpdateManyArrays = false;
    this._pendingManyArrayUpdates = [];
  }
  get identifierCache() {
    return identifierCacheFor(this._store);
  }

  /**
   * Exists so that DefaultRecordData can check for model types
   * in DEBUG for relationships. Should be refactored away.
   *
   * @internal
   */
  _hasModelFor(type) {
    return this._store._hasModelFor(type);
  }

  /**
   * @internal
   */
  _scheduleManyArrayUpdate(identifier, key) {
    var pending = this._pendingManyArrayUpdates = this._pendingManyArrayUpdates || [];
    pending.push(identifier, key);
    if (this._willUpdateManyArrays === true) {
      return;
    }
    this._willUpdateManyArrays = true;
    var backburner = this._store._backburner;
    backburner.join(() => {
      backburner.schedule('syncRelationships', this, this._flushPendingManyArrayUpdates);
    });
  }
  notifyErrorsChange(type, id, lid) {
    var resource = constructResource(type, id, lid);
    var identifier = identifierCacheFor(this._store).getOrCreateRecordIdentifier(resource);
    var internalModel = internalModelFactoryFor(this._store).peek(identifier);
    if (internalModel) {
      internalModel.notifyErrorsChange();
    }
  }
  _flushPendingManyArrayUpdates() {
    if (this._willUpdateManyArrays === false) {
      return;
    }
    var pending = this._pendingManyArrayUpdates;
    this._pendingManyArrayUpdates = [];
    this._willUpdateManyArrays = false;
    var factory = internalModelFactoryFor(this._store);
    for (var i = 0; i < pending.length; i += 2) {
      var identifier = pending[i];
      var _key = pending[i + 1];
      var internalModel = factory.peek(identifier);
      if (internalModel) {
        internalModel.notifyHasManyChange(_key);
      }
    }
  }
  attributesDefinitionFor(type) {
    return this._store._attributesDefinitionFor(type);
  }
  relationshipsDefinitionFor(type) {
    return this._store._relationshipsDefinitionFor(type);
  }
  inverseForRelationship(type, key) {
    var modelClass = this._store.modelFor(type);
    var definition = upgradeForInternal(this.relationshipsDefinitionFor(type)[key]);
    if (false /* CUSTOM_MODEL_CLASS */) {
      if (definition.inverse !== undefined) {
        return definition.inverse;
      } else {
        //TODO add a test for this branch
        if (!definition._inverseKey) {
          return null;
        }
        return definition._inverseKey(this._store, modelClass);
      }
    } else {
      return definition._inverseKey(this._store, modelClass);
    }
  }
  inverseIsAsyncForRelationship(type, key) {
    var modelClass = this._store.modelFor(type);
    var definition = upgradeForInternal(this.relationshipsDefinitionFor(type)[key]);
    if (false /* CUSTOM_MODEL_CLASS */) {
      if (definition.inverse === null) {
        return false;
      }
      if (definition.inverseIsAsync !== undefined) {
        return !!definition.inverseIsAsync;
      } else {
        return definition._inverseIsAsync(this._store, modelClass);
      }
    } else {
      return definition._inverseIsAsync(this._store, modelClass);
    }
  }
  notifyPropertyChange(type, id, lid, key) {
    var resource = constructResource(type, id, lid);
    var identifier = identifierCacheFor(this._store).getOrCreateRecordIdentifier(resource);
    var internalModel = internalModelFactoryFor(this._store).peek(identifier);
    if (internalModel) {
      internalModel.notifyPropertyChange(key);
    }
  }
  notifyHasManyChange(type, id, lid, key) {
    var resource = constructResource(type, id, lid);
    var identifier = identifierCacheFor(this._store).getOrCreateRecordIdentifier(resource);
    this._scheduleManyArrayUpdate(identifier, key);
  }
  notifyBelongsToChange(type, id, lid, key) {
    var resource = constructResource(type, id, lid);
    var identifier = identifierCacheFor(this._store).getOrCreateRecordIdentifier(resource);
    var internalModel = internalModelFactoryFor(this._store).peek(identifier);
    if (internalModel) {
      internalModel.notifyBelongsToChange(key);
    }
  }
  notifyStateChange(type, id, lid, key) {
    var resource = constructResource(type, id, lid);
    var identifier = identifierCacheFor(this._store).getOrCreateRecordIdentifier(resource);
    var internalModel = internalModelFactoryFor(this._store).peek(identifier);
    if (internalModel) {
      internalModel.notifyStateChange(key);
    }
  }
  recordDataFor(type, id, lid) {
    var identifier;
    var isCreate = false;
    if (!id && !lid) {
      isCreate = true;
      identifier = {
        type
      };
    } else {
      var resource = constructResource(type, id, lid);
      identifier = identifierCacheFor(this._store).getOrCreateRecordIdentifier(resource);
    }
    return this._store.recordDataFor(identifier, isCreate);
  }
  setRecordId(type, id, lid) {
    this._store.setRecordId(type, id, lid);
  }
  isRecordInUse(type, id, lid) {
    var resource = constructResource(type, id, lid);
    var identifier = identifierCacheFor(this._store).getOrCreateRecordIdentifier(resource);
    var internalModel = internalModelFactoryFor(this._store).peek(identifier);
    if (!internalModel) {
      return false;
    }
    return internalModel.isRecordInUse();
  }
  disconnectRecord(type, id, lid) {
    var resource = constructResource(type, id, lid);
    var identifier = identifierCacheFor(this._store).getOrCreateRecordIdentifier(resource);
    var internalModel = internalModelFactoryFor(this._store).peek(identifier);
    if (internalModel) {
      internalModel.destroyFromRecordData();
    }
  }
}